import { SummarySearch } from "@/types/OrderItem";
import {
  PaymentOrderAdsSearch,
  PaymentOrderAdsSearchResult,
  PaymentOrderAdsSummaryResult,
} from "@/types/PaymentReceiveADS";
import {
  PaymentReceiveGstDetailSearch,
  PaymentReceiveGstDetailSearchResult,
  PaymentReceiveGstDetailSummaryResult,
} from "@/types/PaymentReceiveGstDetails";
import {
  PaymentReceiveMpFeeRebateSearch,
  PaymentReceiveMpFeeRebateSearchResult,
  PaymentReceiveMpFeeSummaryResult,
} from "@/types/PaymentReceiveMpFeeRebate";
import {
  PaymentReceiveNonOrderSpfSearch,
  PaymentReceiveNonOrderSpfSearchResult,
  PaymentReceiveNonOrderSpfSummaryResult,
} from "@/types/PaymentReceiveNonOrderSpf";
import {
  PaymentOrderSummaryResult,
  PaymentReceiveOrderSearch,
  PaymentReceiveOrderSearchResult,
} from "@/types/PaymentReceiveOrder";
import {
  PaymentReceiveStorageRecallSearch,
  PaymentReceiveStorageRecallSearchResult,
  PaymentReceiveStorageSummaryResult,
} from "@/types/PaymentReceiveStorageRecall";
import {
  PaymentReceiveTcsRecoverySearch,
  PaymentReceiveTcsRecoverySearchResult,
  PaymentReceiveTcsRecoverySummaryResult,
} from "@/types/PaymentReceiveTcsRecovery";
import {
  PaymentReceiveTdsSearch,
  PaymentReceiveTdsSearchResult,
  PaymentReceiveTdsSummaryResult,
} from "@/types/PaymentReceiveTds";
import {
  PaymentValueAddServiceSearch,
  PaymentValueAddServiceSearchResult,
  PaymentValueAddServiceSummaryResult,
} from "@/types/PaymentReceiveValueAddedServices";
import { marketPlaceBaseQueryWrapper } from "@/utils/rtkHelper";
import { createApi } from "@reduxjs/toolkit/query/react";
import { method } from "lodash";

export const PaymentReceiveOrderApi = createApi({
  reducerPath: "PaymentReceiveOrderApi",
  baseQuery: marketPlaceBaseQueryWrapper,
  tagTypes: ["Payment"],
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    getPaymentReceiveOrder: builder.query({
      query: () => `/paymentreceiveorder`,
      providesTags: ["Payment"],
    }),
    uploadPaymentReceiveOrder: builder.mutation({
      query: ({ formData }) => ({
        url: `/document/payment-receive-order/xlsx`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Payment"],
    }),
    getPaymentReceiveMPFeeRebate: builder.query({
      query: () => `/paymentreceivempfeerebate`,
      providesTags: ["Payment"],
    }),
    getPaymentReceiveValueAddedServices: builder.query({
      query: () => `/payment-receive-value-added-services`,
      providesTags: ["Payment"],
    }),
    getPaymentReceiveNonOrderSpf: builder.query({
      query: () => `/payment-receive-non-order-spf`,
      providesTags: ["Payment"],
    }),
    getPaymentReceiveStorageRecall: builder.query({
      query: () => `/payment-receive-storage-recall`,
      providesTags: ["Payment"],
    }),
    getPaymentReceiveTcsRecovery: builder.query({
      query: () => `/payment-receive-tcs-recovery`,
      providesTags: ["Payment"],
    }),
    getPaymentReceiveTds: builder.query({
      query: () => `/payment-receive-tds`,
      providesTags: ["Payment"],
    }),
    getPaymentReceiveAds: builder.query({
      query: () => `/payment-receive-ads`,
      providesTags: ["Payment"],
    }),
    getPaymentReceiveGstDetails: builder.query({
      query: () => `/payment-receive-gst-details`,
      providesTags: ["Payment"],
    }),
    searchPaymentReceiveAds: builder.query<
      PaymentOrderAdsSearchResult,
      PaymentOrderAdsSearch
    >({
      query: (input) => ({
        url: `/payment-receive-ads/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentReceiveAds: builder.mutation<Blob, PaymentOrderAdsSearch>({
      query: (input) => ({
        url: `/payment-receive-ads/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentReceiveAds: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/payment-receive-ads/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    searchReceiveOrders: builder.query<
      PaymentReceiveOrderSearchResult,
      PaymentReceiveOrderSearch
    >({
      query: (input) => ({
        url: `/paymentreceiveorder/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentReceiveOrders: builder.mutation<
      Blob,
      PaymentReceiveOrderSearch
    >({
      query: (input) => ({
        url: `/paymentreceiveorder/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentReceiveOrder: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/paymentreceiveorder/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    searchReceiveMpFeeRebate: builder.query<
      PaymentReceiveMpFeeRebateSearchResult,
      PaymentReceiveMpFeeRebateSearch
    >({
      query: (input) => ({
        url: `/paymentreceivempfeerebate/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentReceiveMpFeeRebate: builder.mutation<
      Blob,
      PaymentReceiveMpFeeRebateSearch
    >({
      query: (input) => ({
        url: `/paymentreceivempfeerebate/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentReceiveMpFeeRebate: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/paymentreceivempfeerebate/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    searchReceiveValueAddedServices: builder.query<
      PaymentValueAddServiceSearchResult,
      PaymentValueAddServiceSearch
    >({
      query: (input) => ({
        url: `/payment-receive-value-added-services/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentValueAddedService: builder.mutation<
      Blob,
      PaymentValueAddServiceSearch
    >({
      query: (input) => ({
        url: `/payment-receive-value-added-services/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentReceiveValueAddedService: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/payment-receive-value-added-services/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    searchNonOrderSpf: builder.query<
      PaymentReceiveNonOrderSpfSearchResult,
      PaymentReceiveNonOrderSpfSearch
    >({
      query: (input) => ({
        url: `/payment-receive-non-order-spf/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentNonOrderSPF: builder.mutation<
      Blob,
      PaymentReceiveNonOrderSpfSearch
    >({
      query: (input) => ({
        url: `/payment-receive-non-order-spf/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentReceiveNonOrderSPF: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/payment-receive-non-order-spf/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    searchStorageRecall: builder.query<
      PaymentReceiveStorageRecallSearchResult,
      PaymentReceiveStorageRecallSearch
    >({
      query: (input) => ({
        url: `/payment-receive-storage-recall/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentStorageRecall: builder.mutation<
      Blob,
      PaymentReceiveStorageRecallSearch
    >({
      query: (input) => ({
        url: `/payment-receive-storage-recall/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentStorageRecall: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/payment-receive-storage-recall/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    searchTcsRecovery: builder.query<
      PaymentReceiveTcsRecoverySearchResult,
      PaymentReceiveTcsRecoverySearch
    >({
      query: (input) => ({
        url: `/payment-receive-tcs-recovery/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentTCSRecovery: builder.mutation<
      Blob,
      PaymentReceiveTcsRecoverySearch
    >({
      query: (input) => ({
        url: `/payment-receive-tcs-recovery/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentTCSRecovery: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/payment-receive-tcs-recovery/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    searchPaymentTds: builder.query<
      PaymentReceiveTdsSearchResult,
      PaymentReceiveTdsSearch
    >({
      query: (input) => ({
        url: `/payment-receive-tds/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentTDS: builder.mutation<Blob, PaymentReceiveTdsSearch>({
      query: (input) => ({
        url: `/payment-receive-tds/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentTDS: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/paymepayment-receive-tdsntreceiveorder/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    searchGstDetails: builder.query<
      PaymentReceiveGstDetailSearchResult,
      PaymentReceiveGstDetailSearch
    >({
      query: (input) => ({
        url: `/payment-receive-gst-details/search`,
        method: "POST",
        body: { ...input, export: false },
      }),
      providesTags: ["Payment"],
    }),
    exportPaymentGSTDetails: builder.mutation<
      Blob,
      PaymentReceiveGstDetailSearch
    >({
      query: (input) => ({
        url: `/payment-receive-gst-details/search`,
        method: "POST",
        body: { ...input, export: true },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    deletePaymentGstDetails: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: "/payment-receive-gst-details/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Payment"],
    }),
    summaryPaymentOrders: builder.query<
      PaymentOrderSummaryResult,
      PaymentReceiveOrderSearch
    >({
      query: (input) => ({
        url: `/paymentreceiveorder/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
    summaryFeeRebate: builder.query<
      PaymentReceiveMpFeeSummaryResult,
      PaymentReceiveMpFeeRebateSearch
    >({
      query: (input) => ({
        url: `/paymentreceivempfeerebate/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
    summaryServices: builder.query<
      PaymentValueAddServiceSummaryResult,
      PaymentValueAddServiceSearch
    >({
      query: (input) => ({
        url: `/payment-receive-value-added-services/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
    summarySPF: builder.query<
      PaymentReceiveNonOrderSpfSummaryResult,
      PaymentReceiveNonOrderSpfSearch
    >({
      query: (input) => ({
        url: `/payment-receive-non-order-spf/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
    summaryStorageRecall: builder.query<
      PaymentReceiveStorageSummaryResult,
      PaymentReceiveStorageRecallSearch
    >({
      query: (input) => ({
        url: `/payment-receive-storage-recall/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
    summaryTcs: builder.query<
      PaymentReceiveTcsRecoverySummaryResult,
      PaymentReceiveTcsRecoverySearch
    >({
      query: (input) => ({
        url: `/payment-receive-tcs-recovery/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
    summaryTds: builder.query<
      PaymentReceiveTdsSummaryResult,
      PaymentReceiveTdsSearch
    >({
      query: (input) => ({
        url: `/payment-receive-tds/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
    summaryAds: builder.query<
      PaymentOrderAdsSummaryResult,
      PaymentOrderAdsSearch
    >({
      query: (input) => ({
        url: `/payment-receive-ads/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
    summaryGstDetail: builder.query<
      PaymentReceiveGstDetailSummaryResult,
      PaymentReceiveGstDetailSearch
    >({
      query: (input) => ({
        url: `/payment-receive-gst-details/summary`,
        method: "POST",
        body: input,
      }),
      providesTags: ["Payment"],
    }),
  }),
});

export const {
  useGetPaymentReceiveOrderQuery,
  useUploadPaymentReceiveOrderMutation,
  useGetPaymentReceiveMPFeeRebateQuery,
  useGetPaymentReceiveAdsQuery,
  useGetPaymentReceiveGstDetailsQuery,
  useGetPaymentReceiveNonOrderSpfQuery,
  useGetPaymentReceiveStorageRecallQuery,
  useGetPaymentReceiveTcsRecoveryQuery,
  useGetPaymentReceiveTdsQuery,
  useGetPaymentReceiveValueAddedServicesQuery,
  useSearchPaymentReceiveAdsQuery,
  useLazySearchPaymentReceiveAdsQuery,
  useExportPaymentReceiveAdsMutation,
  useSearchReceiveOrdersQuery,
  useLazySearchReceiveOrdersQuery,
  useExportPaymentReceiveOrdersMutation,
  useSearchGstDetailsQuery,
  useLazyGetPaymentReceiveGstDetailsQuery,
  useExportPaymentGSTDetailsMutation,
  useSearchNonOrderSpfQuery,
  useLazySearchNonOrderSpfQuery,
  useExportPaymentNonOrderSPFMutation,
  useSearchPaymentTdsQuery,
  useLazySearchPaymentTdsQuery,
  useExportPaymentTDSMutation,
  useSearchReceiveMpFeeRebateQuery,
  useLazySearchReceiveMpFeeRebateQuery,
  useExportPaymentReceiveMpFeeRebateMutation,
  useSearchReceiveValueAddedServicesQuery,
  useLazySearchReceiveValueAddedServicesQuery,
  useExportPaymentValueAddedServiceMutation,
  useSearchStorageRecallQuery,
  useLazySearchStorageRecallQuery,
  useExportPaymentStorageRecallMutation,
  useSearchTcsRecoveryQuery,
  useLazySearchTcsRecoveryQuery,
  useExportPaymentTCSRecoveryMutation,
  useSummaryPaymentOrdersQuery,
  useSummaryAdsQuery,
  useSummaryFeeRebateQuery,
  useSummaryGstDetailQuery,
  useSummarySPFQuery,
  useSummaryServicesQuery,
  useSummaryStorageRecallQuery,
  useSummaryTcsQuery,
  useSummaryTdsQuery,
  useDeletePaymentGstDetailsMutation,
  useDeletePaymentReceiveAdsMutation,
  useDeletePaymentReceiveMpFeeRebateMutation,
  useDeletePaymentReceiveNonOrderSPFMutation,
  useDeletePaymentReceiveOrderMutation,
  useDeletePaymentReceiveValueAddedServiceMutation,
  useDeletePaymentStorageRecallMutation,
  useDeletePaymentTCSRecoveryMutation,
  useDeletePaymentTDSMutation,
} = PaymentReceiveOrderApi;
